import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { btnGhost, btnSecondary, errorText, input, inputError, label } from './ui';

const TYPES = ['text', 'select', 'multiselect'];
const DESCRIPTION_MAX = 300;

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `q-${Date.now()}-${Math.random().toString(16).slice(2)}`);

/**
 * Compact editor for extra onboarding questions (same shape as the company
 * custom steps: { id, type: text|select|multiselect, question, description?, options? }).
 * Reorder with Move up / Move down instead of drag so it fits a side panel.
 */
export default function CustomStepsEditor({ steps, onChange, t, error, idPrefix = 'steps' }) {
  const [drafts, setDrafts] = useState({});

  const update = (index, patch) => onChange(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  const remove = (index) => onChange(steps.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  const add = () => onChange([...steps, { id: newId(), type: 'text', question: '', description: '', options: [] }]);

  const addOption = (index) => {
    const step = steps[index];
    const key = step.id || index;
    const value = String(drafts[key] || '').trim();
    if (!value) return;
    const options = step.options || [];
    if (!options.includes(value)) update(index, { options: [...options, value] });
    setDrafts((d) => ({ ...d, [key]: '' }));
  };

  const typeLabel = (type) => {
    if (type === 'select') return t('steps.typeSelect');
    if (type === 'multiselect') return t('steps.typeMultiselect');
    return t('steps.typeText');
  };

  return (
    <div className="space-y-3">
      {steps.length === 0 ? <p className="text-[13px] text-ink-muted">{t('steps.empty')}</p> : null}

      <ol className="space-y-3">
        {steps.map((step, index) => {
          const key = step.id || index;
          const base = `${idPrefix}-${index}`;
          const hasError = error && error.index === index;
          return (
            <li key={key} className={`rounded-lg border bg-canvas p-3 ${hasError ? 'border-[color:var(--danger-text)]' : 'border-line'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                  {t('steps.number', { n: index + 1 })}
                </span>
                <div className="flex items-center">
                  <button type="button" className={`${btnGhost} min-w-[44px] px-0`} onClick={() => move(index, -1)} disabled={index === 0} aria-label={t('steps.moveUp')} title={t('steps.moveUp')}>
                    <ArrowUp size={16} aria-hidden />
                  </button>
                  <button type="button" className={`${btnGhost} min-w-[44px] px-0`} onClick={() => move(index, 1)} disabled={index === steps.length - 1} aria-label={t('steps.moveDown')} title={t('steps.moveDown')}>
                    <ArrowDown size={16} aria-hidden />
                  </button>
                  <button type="button" className={`${btnGhost} text-[color:var(--danger-text)]`} onClick={() => remove(index)}>
                    <Trash2 size={16} aria-hidden />
                    <span className="hidden sm:inline">{t('steps.remove')}</span>
                    <span className="sr-only sm:hidden">{t('steps.remove')}</span>
                  </button>
                </div>
              </div>

              <fieldset className="mt-2">
                <legend className={label}>{t('steps.type')}</legend>
                <div className="flex flex-wrap gap-1" role="radiogroup">
                  {TYPES.map((type) => {
                    const selected = (step.type || 'text') === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => update(index, { type, options: type === 'text' ? [] : step.options || [] })}
                        className={`min-h-[36px] rounded px-3 text-[13px] font-medium border transition duration-120 ${
                          selected ? 'border-forest bg-forest-faint text-forest-ondark' : 'border-line text-ink hover:bg-moss'
                        }`}
                      >
                        {typeLabel(type)}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div className="mt-3">
                <label htmlFor={`${base}-q`} className={label}>{t('steps.question')}</label>
                <input
                  id={`${base}-q`}
                  className={`${input} ${hasError && error.reason === 'question' ? inputError : ''}`}
                  value={step.question || ''}
                  maxLength={200}
                  onChange={(e) => update(index, { question: e.target.value })}
                  placeholder={t('steps.questionPlaceholder')}
                  aria-invalid={hasError && error.reason === 'question' ? 'true' : undefined}
                />
                {hasError && error.reason === 'question' ? <p className={`${errorText} mt-1`}>{t('steps.err.question')}</p> : null}
              </div>

              <div className="mt-3">
                <label htmlFor={`${base}-d`} className={label}>{t('steps.description')}</label>
                <input
                  id={`${base}-d`}
                  className={input}
                  value={step.description || ''}
                  maxLength={DESCRIPTION_MAX}
                  onChange={(e) => update(index, { description: e.target.value })}
                />
              </div>

              {step.type && step.type !== 'text' ? (
                <div className="mt-3">
                  <label htmlFor={`${base}-o`} className={label}>{t('steps.options')}</label>
                  <div className="flex gap-2">
                    <input
                      id={`${base}-o`}
                      className={`${input} flex-1 ${hasError && error.reason === 'options' ? inputError : ''}`}
                      value={drafts[key] || ''}
                      onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(index); } }}
                      placeholder={t('steps.optionPlaceholder')}
                    />
                    <button type="button" className={`${btnSecondary} shrink-0`} onClick={() => addOption(index)}>
                      <Plus size={16} aria-hidden />
                      <span className="hidden sm:inline">{t('steps.addOption')}</span>
                      <span className="sr-only sm:hidden">{t('steps.addOption')}</span>
                    </button>
                  </div>
                  {hasError && error.reason === 'options' ? <p className={`${errorText} mt-1`}>{t('steps.err.options')}</p> : null}
                  {(step.options || []).length > 0 ? (
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {step.options.map((opt, optIndex) => (
                        <li key={`${opt}-${optIndex}`} className="inline-flex items-center gap-1 rounded border border-line bg-surface ps-2 text-[13px] text-ink">
                          {opt}
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded text-ink-muted hover:bg-moss hover:text-ink"
                            onClick={() => update(index, { options: step.options.filter((_, i) => i !== optIndex) })}
                            aria-label={`${t('steps.removeOption')}: ${opt}`}
                          >
                            <X size={14} aria-hidden />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>

      <button type="button" className={btnSecondary} onClick={add}>
        <Plus size={16} aria-hidden />
        {t('steps.add')}
      </button>
    </div>
  );
}
